<template>
  <div>
    <div class="air__utils__heading d-flex justify-content-between align-items-center mb-3">
      <div>
        <h5 class="mb-1 font-weight-bold">ID Card Management</h5>
        <p class="text-muted small mb-0">Generate, customize, preview and print ID cards for students and staff.</p>
      </div>

      <!-- Mode Toggle -->
      <div>
        <a-radio-group v-model="activeMode" buttonStyle="solid" size="large" @change="handleModeChange">
          <a-radio-button value="student">
            <i class="fe fe-users mr-1"></i> Students
          </a-radio-button>
          <a-radio-button value="staff">
            <i class="fe fe-briefcase mr-1"></i> Staff Members
          </a-radio-button>
        </a-radio-group>
      </div>
    </div>

    <div v-if="pagepermission.viewable">
      <!-- Print & Page Settings Panel -->
      <print-settings :settings="printSettings" @change="handleSettingsChange" />

      <!-- Filters & Actions Row -->
      <div class="card border-0 shadow-sm mb-4">
        <div class="card-body py-3">
          <div class="row align-items-center">
            <!-- Student Filters -->
            <template v-if="activeMode === 'student'">
              <!-- Session -->
              <div class="col-md-3 col-12 mb-2">
                <a-select
                  size="large"
                  v-model="selectedSession"
                  style="width: 100%"
                  @change="handleSessionChange"
                  :placeholder="'Select Session'"
                >
                  <a-select-option v-for="schoolSession in schoolSessions" :value="schoolSession.id" :key="schoolSession.id">
                    {{ schoolSession.name }}
                    <small class="text-primary ml-1" v-if="schoolSession.is_current">(Current)</small>
                  </a-select-option>
                </a-select>
              </div>

              <!-- Class -->
              <div class="col-md-3 col-6 mb-2">
                <a-spin :spinning="classSpinning">
                  <a-select
                    size="large"
                    v-model="selectedClass"
                    placeholder="All Classes"
                    style="width: 100%"
                    @change="handleClassSelect"
                    :allowClear="true"
                  >
                    <a-select-option :value="undefined">All Classes</a-select-option>
                    <a-select-option v-for="schoolClass in schoolClasses" :value="schoolClass.id" :key="schoolClass.id">
                      {{ schoolClass.name }}
                    </a-select-option>
                  </a-select>
                </a-spin>
              </div>

              <!-- Section -->
              <div class="col-md-2 col-6 mb-2">
                <a-spin :spinning="sectionSpinning">
                  <a-select
                    size="large"
                    v-model="selectedSection"
                    placeholder="All Sections"
                    style="width: 100%"
                    @change="handleSectionChange"
                    :disabled="!selectedClass"
                    :allowClear="true"
                  >
                    <a-select-option :value="undefined">All Sections</a-select-option>
                    <a-select-option v-for="section in sections" :value="section.id" :key="section.id">
                      {{ section.name }}
                    </a-select-option>
                  </a-select>
                </a-spin>
              </div>
            </template>

            <!-- Staff Filters -->
            <template v-else>
              <!-- Staff Type / Role -->
              <div class="col-md-4 col-12 mb-2">
                <a-select
                  size="large"
                  v-model="selectedStaffType"
                  placeholder="All Staff Types / Roles"
                  style="width: 100%"
                  @change="getStaffList"
                  :allowClear="true"
                >
                  <a-select-option :value="undefined">All Staff Types</a-select-option>
                  <a-select-option v-for="st in staffTypes" :value="st.id" :key="st.id">
                    {{ st.name }}
                  </a-select-option>
                </a-select>
              </div>
            </template>

            <!-- Search Field -->
            <div :class="activeMode === 'student' ? 'col-md-4 col-12' : 'col-md-4 col-12'" class="mb-2">
              <a-input-search
                v-model="search"
                :placeholder="activeMode === 'student' ? 'Search by name, roll, code...' : 'Search by staff name, ID...'"
                size="large"
                @search="handleSearch"
                @change="handleSearch"
                :allowClear="true"
              />
            </div>
          </div>

          <!-- Download Action Bar -->
          <div class="row pt-3 border-top mt-2 align-items-center">
            <div class="col-md-6 col-12 mb-2 mb-md-0">
              <span class="font-weight-bold text-dark">
                Selected: <span class="badge badge-primary px-2 py-1 ml-1" style="font-size: 13px;">{{ selectedIds.length }}</span>
              </span>
              <span class="text-muted small ml-2">of {{ meta.total || data.length }} records</span>
              <a-checkbox
                v-if="activeMode === 'staff' || activeMode === 'student'"
                v-model="includeBackSide"
                class="ml-3"
              >
                <span class="small font-weight-bold">Include Back Side</span>
              </a-checkbox>
            </div>

            <div class="col-md-6 col-12 text-md-right">
              <!-- Download PDF for Selected -->
              <button
                type="button"
                class="btn btn-primary mr-2"
                :disabled="selectedIds.length === 0 || generatingPdf"
                @click="downloadSelectedPdf"
              >
                <a-spin v-if="generatingPdf" size="small" class="mr-1" />
                <i v-else class="fe fe-download mr-1"></i>
                Download PDF ({{ selectedIds.length }})
              </button>

              <!-- Download PDF for All in Filter -->
              <button
                type="button"
                class="btn btn-dark"
                :disabled="generatingAllPdf || (!data || data.length === 0)"
                @click="downloadAllPdf"
              >
                <a-spin v-if="generatingAllPdf" size="small" class="mr-1" />
                <i v-else class="fe fe-file-text mr-1"></i>
                Print All ({{ meta.total || data.length }})
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content: Left List & Right Preview -->
      <div class="card border-0 shadow-sm">
        <div class="card-body">
          <div class="row">
            <!-- Left List Column -->
            <div class="col-md-5 col-lg-4 pr-md-3 border-right">
              <vue-custom-scrollbar class="student-scroll-area">
                <students-list
                  :data="data"
                  :loading="loading"
                  :mode="activeMode"
                  :selectedIds="selectedIds"
                  @update:selectedIds="val => selectedIds = val"
                  @itemSelected="handleItemSelection"
                />
              </vue-custom-scrollbar>

              <!-- Pagination (for students) -->
              <div class="pt-3 border-top" v-if="meta.total && meta.total > pageSize">
                <a-pagination
                  size="small"
                  :total="meta.total"
                  :pageSize.sync="pageSize"
                  v-model="current"
                  @change="onPageChange"
                  :showSizeChanger="true"
                  :pageSizeOptions="['15', '30', '50', '100']"
                  @showSizeChange="onPageSizeChange"
                />
              </div>
            </div>

            <!-- Right Preview Column -->
            <div class="col-md-7 col-lg-8 pl-md-4">
              <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <h6 class="mb-0 font-weight-bold text-dark">
                  <i class="fe fe-eye mr-2 text-primary"></i>
                  {{ activeMode === 'student' ? 'Student Card Live Preview' : 'Staff Card Live Preview' }}
                </h6>

                <!-- Preview Controls -->
                <div v-if="selectedItem" class="d-flex flex-wrap align-items-center mt-2 mt-sm-0">
                  <!-- Staff Front/Back Toggle -->
                  <div v-if="activeMode === 'staff'" class="mr-2 mb-1">
                    <a-radio-group v-model="staffPreviewSide" size="small" buttonStyle="solid" @change="refreshPreview">
                      <a-radio-button value="front">Front</a-radio-button>
                      <a-radio-button value="back">Back</a-radio-button>
                    </a-radio-group>
                  </div>

                  <!-- Zoom Presets -->
                  <div class="btn-group btn-group-sm mr-2 mb-1">
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="previewZoom === '100' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="previewZoom = '100'"
                      title="100% Real Size"
                    >
                      100%
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="previewZoom === '125' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="previewZoom = '125'"
                      title="125% Zoom"
                    >
                      125%
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="previewZoom === '150' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="previewZoom = '150'"
                      title="150% Zoom"
                    >
                      150%
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="previewZoom === '175' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="previewZoom = '175'"
                      title="175% Zoom (Recommended)"
                    >
                      175%
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="previewZoom === '200' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="previewZoom = '200'"
                      title="200% Zoom (Large)"
                    >
                      200%
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="previewZoom === 'Fit' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="previewZoom = 'Fit'"
                      title="Fit to Container"
                    >
                      Fit
                    </button>
                  </div>

                  <!-- Fullscreen Modal Button -->
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary mr-2 mb-1"
                    @click="fullScreenPreviewVisible = true"
                    title="Enlarge Card Preview"
                  >
                    <i class="fe fe-maximize-2"></i>
                  </button>

                  <!-- Download Single -->
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary mb-1"
                    @click="downloadSinglePdf"
                    :disabled="previewLoading"
                  >
                    <i class="fe fe-download mr-1"></i> Download
                  </button>
                </div>
              </div>

              <!-- Preview Display Area -->
              <div class="card bg-light border text-center p-3" style="min-height: 600px; border-radius: 8px;">
                <a-spin :spinning="previewLoading">
                  <div v-if="previewPdfBlobUrl" class="preview-container">
                    <iframe
                      :key="`preview_${previewPdfBlobUrl}_${previewZoom}_${staffPreviewSide}`"
                      :src="formattedPreviewUrl"
                      :style="previewIframeStyle"
                      class="preview-iframe shadow-sm"
                      title="ID Card Preview"
                    ></iframe>
                  </div>
                  <div v-else class="text-center py-5 text-muted">
                    <div style="font-size: 48px; color: #cbd5e1;" class="mb-3">
                      <i class="fe fe-credit-card"></i>
                    </div>
                    <h6 class="font-weight-bold text-secondary">No Card Selected</h6>
                    <p class="small text-muted mb-0">Select any student or staff member from the list to preview their ID card.</p>
                  </div>
                </a-spin>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Enlarge / Full Preview Modal -->
      <a-modal
        v-model="fullScreenPreviewVisible"
        :title="activeMode === 'student' ? 'Student ID Card - Full Preview' : 'Staff ID Card - Full Preview'"
        :footer="null"
        width="560px"
        centered
      >
        <div class="text-center py-2">
          <iframe
            v-if="formattedPreviewUrl && fullScreenPreviewVisible"
            :key="`modal_${previewPdfBlobUrl}_${previewZoom}_${staffPreviewSide}`"
            :src="formattedPreviewUrl"
            style="width: 460px; height: 680px; border: 1px solid #cbd5e1; border-radius: 8px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);"
            title="Large Card Preview"
          ></iframe>
        </div>
      </a-modal>
    </div>

    <!-- No Permission View -->
    <div class="row justify-content-center py-5" v-else>
      <div class="col-md-6 text-center">
        <a-spin :spinning="permissionCheckSpinning">
          <div v-if="!permissionCheckSpinning" class="alert alert-danger shadow-sm">
            <i class="fe fe-alert-triangle mr-2"></i> You don't have permission to access the ID Card module.
          </div>
        </a-spin>
      </div>
    </div>
  </div>
</template>

<script>
import StudentsList from '@/components/idcard/List/index'
import PrintSettings from '@/components/idcard/PrintSettings/index'
import vueCustomScrollbar from 'vue-custom-scrollbar'
import { debounce, find } from 'lodash'

export default {
  components: {
    StudentsList,
    PrintSettings,
    vueCustomScrollbar,
  },
  beforeMount() {
    this.permissionCheckSpinning = true
    const rc = this.$roleutility.checkpermission('idcard', 22)
    rc.then((data) => {
      this.pagepermission = data.pagePermission
      this.permissionCheckSpinning = false
    })
  },
  mounted() {
    this.loadSessions()
    this.loadStaffTypes()
    this.loadSchoolSettings()
  },
  created() {
    this.handleSearch = debounce(this.handleSearch, 400)
  },
  data() {
    return {
      activeMode: 'student', // 'student' or 'staff'
      loading: false,
      previewLoading: false,
      generatingPdf: false,
      generatingAllPdf: false,
      data: [],
      meta: {},
      selectedIds: [],
      selectedItem: null,
      previewPdfBlobUrl: null,
      previewZoom: '175',
      fullScreenPreviewVisible: false,
      staffPreviewSide: 'front',
      includeBackSide: true,

      // Filter state
      schoolSessions: [],
      selectedSession: undefined,
      schoolClasses: [],
      selectedClass: undefined,
      sections: [],
      selectedSection: undefined,
      staffTypes: [],
      selectedStaffType: undefined,
      search: null,

      // Pagination
      current: 1,
      pageSize: 15,

      // Loaders
      classSpinning: false,
      sectionSpinning: false,
      permissionCheckSpinning: true,

      // Settings
      printSettings: {
        paper_size: 'A4',
        orientation: 'portrait',
        cards_per_row: 2,
        cards_per_column: 3,
        margin_top_mm: 5.0,
        margin_left_mm: 5.0,
        gap_horizontal_mm: 4.0,
        gap_vertical_mm: 4.0,
        include_background: true,
      },

      pagepermission: {
        insertable: false,
        editable: false,
        viewable: false,
        deletable: false,
      },
    }
  },
  computed: {
    formattedPreviewUrl() {
      if (!this.previewPdfBlobUrl) return null
      return `${this.previewPdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`
    },
    previewIframeStyle() {
      const zoomMap = {
        '100': { width: '240px', height: '380px' },
        '125': { width: '300px', height: '475px' },
        '150': { width: '360px', height: '570px' },
        '175': { width: '420px', height: '665px' },
        '200': { width: '480px', height: '760px' },
        'Fit': { width: '100%', height: '620px' },
      }
      return zoomMap[this.previewZoom] || zoomMap['175']
    },
  },
  methods: {
    handleModeChange(e) {
      this.activeMode = e.target.value
      this.selectedIds = []
      this.selectedItem = null
      this.previewPdfBlobUrl = null
      this.search = null
      this.current = 1

      if (this.activeMode === 'student') {
        this.getStudents()
      } else {
        this.getStaffList()
      }
    },

    loadSessions() {
      this.$http.get('/api/session').then(({ data }) => {
        this.schoolSessions = data.data
        const curr = find(data.data, ['is_current', 1])
        this.selectedSession = curr ? curr.id : (data.data[0] ? data.data[0].id : undefined)
        this.getClasses()
        this.getStudents()
      }).catch(console.error)
    },

    loadStaffTypes() {
      this.$http.get('/api/staff_type').then(({ data }) => {
        this.staffTypes = data.data || []
      }).catch(console.error)
    },

    loadSchoolSettings() {
      this.$http.get('/api/idcard/settings').then(({ data }) => {
        if (data.data) {
          this.printSettings = { ...this.printSettings, ...data.data }
        }
      }).catch(console.error)
    },

    handleSettingsChange(newSettings) {
      this.printSettings = { ...this.printSettings, ...newSettings }
      if (this.selectedItem) {
        this.refreshPreview()
      }
    },

    getClasses() {
      this.classSpinning = true
      this.$http.get('/api/class', {
        params: { session_id: this.selectedSession },
      }).then(({ data }) => {
        this.schoolClasses = data.data || []
        this.classSpinning = false
      }).catch((err) => {
        this.classSpinning = false
        console.error(err)
      })
    },

    handleClassSelect(value) {
      this.selectedSection = undefined
      this.current = 1
      if (value) {
        this.sectionSpinning = true
        this.$http.get(`/api/section/class/${value}`, {
          params: { session_id: this.selectedSession },
        }).then(({ data }) => {
          this.sections = data.data || []
          this.sectionSpinning = false
        }).catch(() => {
          this.sectionSpinning = false
        })
      } else {
        this.sections = []
      }
      this.getStudents()
    },

    handleSectionChange() {
      this.current = 1
      this.getStudents()
    },

    handleSessionChange(value) {
      this.selectedSession = value
      this.selectedClass = undefined
      this.selectedSection = undefined
      this.current = 1
      this.getClasses()
      this.getStudents()
    },

    handleSearch() {
      this.current = 1
      if (this.activeMode === 'student') {
        this.getStudents()
      } else {
        this.getStaffList()
      }
    },

    getStudents() {
      this.loading = true
      this.$http.get('/api/student', {
        params: {
          limit: this.pageSize,
          page: this.current,
          search: this.search,
          class_id: this.selectedClass,
          section_id: this.selectedSection,
          session_id: this.selectedSession,
        },
      }).then(({ data }) => {
        this.loading = false
        this.data = data.data || []
        this.meta = data.meta || {}
      }).catch((err) => {
        this.loading = false
        console.error(err)
      })
    },

    getStaffList() {
      this.loading = true
      this.$http.get('/api/staff', {
        params: {
          staff_type_id: this.selectedStaffType,
          search: this.search,
          limit: 100,
        },
      }).then(({ data }) => {
        this.loading = false
        this.data = data.data || []
        this.meta = data.meta || { total: (data.data || []).length }
      }).catch((err) => {
        this.loading = false
        console.error(err)
      })
    },

    onPageChange(page) {
      this.current = page
      this.getStudents()
    },

    onPageSizeChange(current, size) {
      this.pageSize = size
      this.current = 1
      this.getStudents()
    },

    handleItemSelection(item) {
      this.selectedItem = item
      this.refreshPreview()
    },

    refreshPreview() {
      if (!this.selectedItem) return

      this.previewLoading = true
      let url = ''
      let params = {
        include_background: this.printSettings.include_background,
      }

      if (this.activeMode === 'student') {
        url = `/api/idcard/preview/student/${this.selectedItem.id}`
        params.session_id = this.selectedSession
      } else {
        url = `/api/idcard/preview/staff/${this.selectedItem.id}`
        params.side = this.staffPreviewSide
      }

      this.$http.get(url, {
        params,
        responseType: 'blob',
      }).then((response) => {
        this.previewLoading = false
        if (this.previewPdfBlobUrl) {
          URL.revokeObjectURL(this.previewPdfBlobUrl)
        }
        const blob = new Blob([response.data], { type: 'application/pdf' })
        this.previewPdfBlobUrl = URL.createObjectURL(blob)
      }).catch((err) => {
        this.previewLoading = false
        this.$message.error('Failed to load card preview.')
        console.error(err)
      })
    },

    downloadSinglePdf() {
      if (!this.previewPdfBlobUrl || !this.selectedItem) return
      const code = this.selectedItem.student_code || this.selectedItem.staff_id || 'card'
      const name = `${this.selectedItem.first_name}_${this.selectedItem.last_name || ''}`
      const a = document.createElement('a')
      a.href = this.previewPdfBlobUrl
      a.download = `IDCard_${name}_${code}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },

    downloadSelectedPdf() {
      if (this.selectedIds.length === 0) return

      this.generatingPdf = true
      const payload = {
        ...this.printSettings,
        include_back_side: this.includeBackSide,
      }

      let endpoint = ''
      if (this.activeMode === 'student') {
        endpoint = '/api/idcard/generate/students/pdf'
        payload.session_id = this.selectedSession
        payload.student_ids = this.selectedIds
      } else {
        endpoint = '/api/idcard/generate/staff/pdf'
        payload.staff_ids = this.selectedIds
      }

      this.$http.post(endpoint, payload, { responseType: 'blob' })
        .then((response) => {
          this.generatingPdf = false
          this.triggerBlobDownload(response.data, `${this.activeMode}_idcards_selected.pdf`)
        })
        .catch((err) => {
          this.generatingPdf = false
          this.$message.error('Failed to generate PDF download.')
          console.error(err)
        })
    },

    downloadAllPdf() {
      this.generatingAllPdf = true
      const payload = {
        ...this.printSettings,
        include_back_side: this.includeBackSide,
      }

      let endpoint = ''
      if (this.activeMode === 'student') {
        endpoint = '/api/idcard/generate/students/pdf'
        payload.session_id = this.selectedSession
        payload.class_id = this.selectedClass
        payload.section_id = this.selectedSection
        payload.search = this.search
      } else {
        endpoint = '/api/idcard/generate/staff/pdf'
        payload.staff_type_id = this.selectedStaffType
        payload.search = this.search
      }

      this.$http.post(endpoint, payload, { responseType: 'blob' })
        .then((response) => {
          this.generatingAllPdf = false
          this.triggerBlobDownload(response.data, `${this.activeMode}_idcards_all.pdf`)
        })
        .catch((err) => {
          this.generatingAllPdf = false
          this.$message.error('Failed to generate full PDF download.')
          console.error(err)
        })
    },

    triggerBlobDownload(blobData, defaultFileName) {
      const blob = new Blob([blobData], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = defaultFileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      this.$message.success('Download started successfully!')
    },
  },
}
</script>

<style lang="scss" scoped>
.student-scroll-area {
  position: relative;
  height: 68vh;
  padding-right: 0.5rem;
  overflow-y: auto;
}

.preview-container {
  width: 100%;
  min-height: 580px;
  max-height: 80vh;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 10px 4px;
}

.preview-iframe {
  transition: width 0.2s ease, height 0.2s ease;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background-color: #ffffff;
}
</style>
